import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Invoice } from '../types';
import { api } from '../services/api';
import './InvoiceDetailPage.css';

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const getStatusBadgeClass = (invoice: Invoice): string => {
  if (invoice.status === 'void') return 'void';
  if (invoice.datePaid || invoice.status === 'paid') return 'paid';
  if (invoice.status === 'overdue') return 'overdue';
  return 'unpaid';
};

const getStatusLabel = (invoice: Invoice): string => {
  if (invoice.status === 'void') return 'Void';
  if (invoice.datePaid || invoice.status === 'paid') return 'Paid';
  if (invoice.status === 'overdue') return 'Overdue';
  return 'Unpaid';
};

export const InvoiceDetailPage = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (invoiceId) {
      fetchInvoice(invoiceId);
    }
  }, [invoiceId]);

  const fetchInvoice = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getInvoiceById(id);
      setInvoice(data);
    } catch (err) {
      setError('Failed to load invoice. Please try again later.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="invoice-detail-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="invoice-detail-page">
        <div className="error">
          <p>{error || 'Invoice not found.'}</p>
          <button onClick={() => navigate('/billing')} className="retry-button">
            Back to Billing
          </button>
        </div>
      </div>
    );
  }

  const balance = invoice.balance ?? (invoice.datePaid ? 0 : invoice.amount);

  return (
    <div className="invoice-detail-page">
      <button className="back-link" onClick={() => navigate('/billing')}>
        ← Back to Billing
      </button>

      <div className="page-header">
        <div className="invoice-header-row">
          <h1>Invoice {invoice.invoiceNumber}</h1>
          <span className={`status-badge ${getStatusBadgeClass(invoice)}`}>
            {getStatusLabel(invoice)}
          </span>
        </div>
        {invoice.customerName && (
          <p className="subtitle">{invoice.customerName}</p>
        )}
      </div>

      <div className="detail-sections">
        {/* Invoice Details + Amount Summary */}
        <div className="detail-section">
          <h3 className="section-title">Invoice Details</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Invoice Number</span>
              <span className="info-value">{invoice.invoiceNumber}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Date Invoiced</span>
              <span className="info-value">{formatDate(invoice.dateInvoiced)}</span>
            </div>
            {invoice.dueDate && (
              <div className="info-item">
                <span className="info-label">Due Date</span>
                <span className="info-value">{formatDate(invoice.dueDate)}</span>
              </div>
            )}
            <div className="info-item">
              <span className="info-label">Date Paid</span>
              <span className="info-value">{formatDate(invoice.datePaid)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Invoice Total</span>
              <span className="info-value">{formatCurrency(invoice.amount)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Balance Due</span>
              <span className={`info-value ${balance === 0 ? 'balance-zero' : 'balance-due'}`}>
                {formatCurrency(balance)}
              </span>
            </div>
          </div>
        </div>

        {/* Line Items */}
        {invoice.lineItems && invoice.lineItems.length > 0 && (
          <div className="detail-section">
            <h3 className="section-title">Line Items</h3>
            <div className="line-items-table-wrapper">
              <table className="line-items-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="num-col">Qty</th>
                    <th className="num-col">Unit Price</th>
                    <th className="num-col">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lineItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.description}</td>
                      <td className="num-col">{item.quantity}</td>
                      <td className="num-col">{formatCurrency(item.unitPrice)}</td>
                      <td className="num-col">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="line-items-total">
                    <td colSpan={3}>Total</td>
                    <td className="num-col">{formatCurrency(invoice.amount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="detail-section">
            <h3 className="section-title">Notes</h3>
            <p className="invoice-notes">{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};
